import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { parseApiDate } from '@propertypal/shared/src/services/dayjs';
import {
  IGalleryGallery,
  IKeyHighlightsKeyHighlights,
  IIssuuEmbedIssuuEmbed,
  IRichTextRichText,
  IButtonButton,
} from '@propertypal/shared/src/types/strapi';
import { ContentHubArticle } from '@propertypal/shared/src/types/strapi/overrides';
import React from 'react';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { Box } from '../layout';
import { Bold, Heading, Text } from '../typography';
import {
  ArticleAuthor,
  ArticleButton,
  ArticleButtonLink,
  ArticlePublished,
  ArticleRichText,
  ArticleSubtitle,
  AuthorPic,
  KeyHighlight,
  KeyHighlightDesc,
  KeyHighlightTitle,
  KeyHighlightTitleText,
  KeyHighlights,
  KeyHighlightsContainer,
  KeyHighlightsText,
} from './Article.style';
import ArticleGallery from './ArticleGallery';
import ArticleNotFound from './ArticleNotFound';

interface ArticleContentProps {
  data: ContentHubArticle | null;
}

const ArticleContent: React.FC<ArticleContentProps> = ({ data }) => {
  const theme = useTheme();

  return (
    <div>
      {data && (data.attributes?.title != null || data.attributes?.articleTitle != null) && (
        <Heading mb={35}>
          <span data-testid="article-heading">
            <span>{data.attributes?.articleTitle || data?.attributes.title}</span>
            {data?.attributes?.articleSubTitle?.text && (
              <ArticleSubtitle
                data-testid="article-subtitle"
                colour={data?.attributes?.articleSubTitle?.colour}
                italicise={data?.attributes?.articleSubTitle?.italicise}
              >
                {' '}
                {data?.attributes?.articleSubTitle?.text}
              </ArticleSubtitle>
            )}
          </span>
          {data?.attributes.publishedAt && (
            <ArticlePublished>
              <FontAwesomeIcon icon={faCalendar} />
              <span data-testid="article-published-date">
                Published {parseApiDate(data?.attributes.publishedAt).format('DD MMM YYYY')}
              </span>
            </ArticlePublished>
          )}
        </Heading>
      )}
      {data && data.attributes.author?.data?.attributes && (
        <ArticleAuthor data-testid="article-author">
          {data.attributes.author.data.attributes.profilePicture?.data?.attributes?.url && (
            <AuthorPic
              src={data.attributes.author.data.attributes.profilePicture.data.attributes.url}
              alt={data.attributes.author.data.attributes.fullname!}
              data-testid="article-author-img"
            />
          )}
          <div>
            <Text fontSize={12} color={theme.backgroundMid} textTransform="uppercase">
              Commentary By
            </Text>
            <Bold>{data.attributes.author.data.attributes.fullname}</Bold>
            <Text color={theme.primary}>{data.attributes.author.data.attributes.jobTitle}</Text>
          </div>
        </ArticleAuthor>
      )}
      {data && data.attributes?.content != null && data.attributes?.content?.length > 0 ? (
        data.attributes?.content.map((c) => {
          if (Object.prototype.hasOwnProperty.call(c, 'content')) {
            const ac = c as IRichTextRichText;
            if (ac?.content != null)
              return (
                <ArticleRichText
                  // eslint-disable-next-line no-underscore-dangle
                  key={`${ac.id}${ac.__component}`}
                  // add target="_blank" to external links, unable to do this via the rich text editor within Strapi
                  html={ac?.content?.replace(/<a href="([^"]+)"/g, (match, href) => {
                    if (
                      !href.startsWith('/') &&
                      !href.startsWith('https://www.propertypal') &&
                      !href.startsWith('https://propertypal')
                    ) {
                      return `<a target="_blank" rel="noreferrer" href="${href}"`;
                    }
                    return match;
                  })}
                />
              );
          }
          if (Object.prototype.hasOwnProperty.call(c, 'code')) {
            const ac = c as IIssuuEmbedIssuuEmbed;
            if (ac?.code != null)
              return (
                <div
                  // eslint-disable-next-line no-underscore-dangle
                  key={`${ac.id}${ac.__component}`}
                  data-testid="pdf-viewer"
                  dangerouslySetInnerHTML={{ __html: ac.code }}
                />
              );
          }
          if (Object.prototype.hasOwnProperty.call(c, 'highlights')) {
            const ac = c as IKeyHighlightsKeyHighlights;
            if (ac?.highlights?.length) {
              return (
                // eslint-disable-next-line no-underscore-dangle
                <KeyHighlightsContainer key={`${ac.id}${ac.__component}`} data-testid="key-highlights">
                  <Text fontWeight="bold" mb={15}>
                    {ac?.title || 'Key highlights from the report include:'}
                  </Text>
                  <KeyHighlights>
                    {ac?.highlights.map((highlight) => (
                      <KeyHighlight key={highlight.id} data-testid="key-highlight">
                        {highlight.icon && (
                          <div>
                            <img
                              src={highlight.icon?.data?.attributes.url}
                              alt={
                                highlight.icon?.data?.attributes.alternativeText ||
                                `${data.attributes.title} Key Highlight`
                              }
                            />
                          </div>
                        )}
                        <KeyHighlightTitle>
                          {highlight.subtitle && (
                            <Text fontSize={18} fontWeight="bold">
                              {highlight.subtitle}
                            </Text>
                          )}
                          <KeyHighlightTitleText>{highlight.title}</KeyHighlightTitleText>
                          {highlight.smallTitle && <Text fontSize={12}>{highlight.smallTitle}</Text>}
                        </KeyHighlightTitle>
                        <KeyHighlightDesc>
                          <KeyHighlightsText html={highlight.description} />
                        </KeyHighlightDesc>
                      </KeyHighlight>
                    ))}
                  </KeyHighlights>
                </KeyHighlightsContainer>
              );
            }
          }
          if (Object.prototype.hasOwnProperty.call(c, 'gallery')) {
            const ac = c as IGalleryGallery;
            if (ac?.gallery != null && ac.gallery.data?.length)
              return (
                <ArticleGallery
                  // eslint-disable-next-line no-underscore-dangle
                  key={`${ac.id}${ac.__component}`}
                  gallery={ac.gallery.data}
                  alt={data?.attributes.title!}
                />
              );
          }
          if (Object.prototype.hasOwnProperty.call(c, 'buttonText')) {
            const ac = c as IButtonButton;
            if (ac?.buttonText != null) {
              const externalLink =
                !ac.buttonLink?.startsWith('/') &&
                !ac.buttonLink?.startsWith('https://www.propertypal') &&
                !ac.buttonLink?.startsWith('https://propertypal');
              return (
                // eslint-disable-next-line no-underscore-dangle
                <Box mt={40} key={`${ac.id}${ac.__component}`} data-testid="article-button">
                  {ac?.buttonTitle && <ArticleRichText html={ac.buttonTitle} />}
                  <ArticleButtonLink
                    href={ac.buttonLink || '#'}
                    target={externalLink ? '_blank' : undefined}
                    rel={externalLink ? 'noreferrer' : undefined}
                  >
                    <ArticleButton>{ac.buttonText}</ArticleButton>
                  </ArticleButtonLink>
                </Box>
              );
            }
          }
          return null;
        })
      ) : (
        <ArticleNotFound />
      )}
    </div>
  );
};

export default ArticleContent;
